import { coffreCanPreviewFile } from "./coffrePreview.js";
import { coffreCanOpenInOffice } from "./coffreOffice.js";
import {
  coffreSharedBrowseLevel,
  coffreSharedAllowWrite,
  COFFRE_SHARED_ZONE_LABEL,
  COFFRE_SHARED_OWNER_PATH_PREFIX,
} from "./coffreAppConstants.js";

/** Entrée Coffre à partir d’un rappel (sidebar / navigation). */
export function coffreReminderToEntry(reminder) {
  if (!reminder?.item_path) return null;
  if (reminder.is_shortcut) {
    return {
      path: reminder.item_path,
      kind: "shortcut",
      is_shortcut: true,
      name: reminder.item_name || reminder.item_path.split("/").filter(Boolean).pop() || reminder.item_path,
      shortcut_target: reminder.shortcut_target ?? null,
      shortcut_broken: Boolean(reminder.shortcut_broken),
      needs_unlock: Boolean(reminder.needs_unlock),
      is_protected: Boolean(reminder.is_protected),
    };
  }
  return {
    path: reminder.item_path,
    kind: reminder.item_kind === "folder" ? "folder" : "file",
    name: reminder.item_name || reminder.item_path.split("/").filter(Boolean).pop() || reminder.item_path,
    shared_owner_id: reminder.shared_owner_id ?? undefined,
  };
}

export function applyRenamedPathPrefix(oldPath, newPath, p) {
  if (!p) return p;
  if (p === oldPath) return newPath;
  if (p.startsWith(`${oldPath}/`)) return newPath + p.slice(oldPath.length);
  return p;
}

export function cn(...parts) {
  return parts.filter(Boolean).join(" ");
}

/** Favori Coffre (liste API ou raccourci sidebar). */
export function coffreEntryIsFavorite(entry) {
  const v = entry?.is_favorite;
  return v === true || v === 1 || v === "1";
}

/** Entrée raccourci (alias vers une autre cible). */
export function coffreEntryIsShortcut(entry) {
  return entry?.kind === "shortcut" || Boolean(entry?.is_shortcut);
}

const COFFRE_CRM_ROOTS = ["CRM clients", "CRM interne", "Mon CRM", "Interne"];

function coffreRel(path) {
  return String(path ?? "").replace(/\\/g, "/").replace(/^\/+|\/+$/g, "");
}

/** Dossier réservé à la racine du Coffre : le CRM s’en sert, il ne se déplace pas. */
export function coffreEntryIsCrmHome(entry) {
  return COFFRE_CRM_ROOTS.includes(coffreRel(entry?.path));
}

export const COFFRE_CRM_HOME_LOCKED = "Le dossier CRM clients est réservé. Il ne peut pas être renommé, déplacé ni supprimé.";
export const COFFRE_INTERNE_HOME_LOCKED = "Le dossier CRM interne est réservé. Il ne peut pas être renommé, déplacé ni supprimé.";
export const COFFRE_CRM_FICHE_LOCKED = "Ce dossier suit une fiche CRM. Il s’archive et se supprime depuis le CRM.";
export const COFFRE_CRM_GUEST_DELETE = "Seul l’auteur peut supprimer dans le dossier de la fiche.";

export function coffrePathIsCrmFicheTree(path) {
  const rel = coffreRel(path);
  return COFFRE_CRM_ROOTS.some((root) => rel === root || rel.startsWith(`${root}/`));
}

/** Un collègue ne supprime pas dans le dossier d’une fiche qu’on lui a partagée. */
export function coffreGuestCrmDeleteBlocked(zone, entry) {
  if (zone !== "shared" && entry?.shared_owner_id == null) return false;
  return coffrePathIsCrmFicheTree(entry?.path);
}

export function coffreEntryIsCrmReserved(entry) {
  return coffreEntryIsCrmHome(entry) || Boolean(entry?.crm_reserved);
}

export function coffreEntryCrmLockMessage(entry) {
  const rel = coffreRel(entry?.path);
  if (rel === "CRM interne" || rel === "Interne") return COFFRE_INTERNE_HOME_LOCKED;
  if (coffreEntryIsCrmHome(entry)) return COFFRE_CRM_HOME_LOCKED;
  if (entry?.crm_reserved) return COFFRE_CRM_FICHE_LOCKED;
  return "";
}

/** Affichage icône dossier vs fichier (raccourci → selon la cible). */
export function coffreEntryDisplaysAsFolder(entry) {
  if (coffreEntryIsShortcut(entry)) {
    return entry?.shortcut_target?.kind === "folder";
  }
  return entry?.kind === "folder";
}

/** Entrée résolue pour téléchargement / aperçu (raccourci → cible). */
export function coffreEntryResolveShortcut(entry) {
  if (!coffreEntryIsShortcut(entry) || entry?.shortcut_broken) return entry;
  const t = entry.shortcut_target;
  if (!t?.path) return entry;
  return {
    ...entry,
    path: t.path,
    name: t.name || entry.name,
    kind: t.kind === "folder" ? "folder" : "file",
    is_shortcut: false,
    kind_shortcut_resolved: true,
    shared_owner_id: t.zone === "shared" && t.owner_id ? t.owner_id : entry.shared_owner_id,
  };
}

/** Options téléchargement — tient compte des raccourcis vers partages. */
export function coffreEntryDownloadOpts(entry, browseZone = "vault", sharedOwnerId = null) {
  const resolved = coffreEntryResolveShortcut(entry);
  const ownerId = coffreEntrySharedOwnerId(resolved, sharedOwnerId);
  if (ownerId != null) {
    return { zone: "shared", ownerId };
  }
  if (browseZone === "ged") {
    return { zone: "ged" };
  }
  if (browseZone === "facturx_legal") {
    return { zone: "facturx_legal" };
  }
  return { zone: "vault" };
}

/** Épingler les favoris en tête en conservant l’ordre relatif des deux groupes. */
export function coffrePartitionFavoritesFirst(entries, enabled = true) {
  if (!enabled || !Array.isArray(entries) || entries.length < 2) return entries;
  const favorites = [];
  const others = [];
  for (const entry of entries) {
    if (coffreEntryIsFavorite(entry)) favorites.push(entry);
    else others.push(entry);
  }
  if (favorites.length === 0) return entries;
  return [...favorites, ...others];
}

/** Affichage taille fichier (octets → Ko, Mo, Go…). */
export function formatStorageBytes(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x < 0) return "—";
  if (x < 1024) return `${Math.round(x)} o`;
  const u = ["Ko", "Mo", "Go", "To"];
  let v = x / 1024;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i += 1;
  }
  const dec = i === 0 ? 0 : v >= 10 ? 0 : 1;
  return `${v.toLocaleString("fr-FR", { maximumFractionDigits: dec })} ${u[i]}`;
}

/** Chemin dossier parent d’un élément partagé ("" = racine coffre propriétaire). */
export function coffreSharedListParentPath(entryPath) {
  const p = String(entryPath ?? "").replace(/\\/g, "/").trim();
  if (!p) return null;
  const i = p.lastIndexOf("/");
  return i < 0 ? "" : p.slice(0, i);
}

/** Cible de rechargement après édition en zone partagée (Avec moi). */
export function coffreSharedRefreshTarget(currentPath, filePath) {
  const cp = String(currentPath ?? "").trim();
  if (cp) return { kind: "folder", path: cp };
  const parent = coffreSharedListParentPath(filePath);
  if (parent === null || parent === "") return { kind: "home" };
  return { kind: "folder", path: parent };
}

/** Fil d’Ariane zone partagée (global → propriétaire → contenu). */
export function coffreSharedBreadcrumbView(browseZone, sharedOwnerId, currentPath, shareRoot, ownerLabel, zoneLabel) {
  const level = coffreSharedBrowseLevel(browseZone, sharedOwnerId, currentPath);
  const zone = zoneLabel || COFFRE_SHARED_ZONE_LABEL;
  const owner = String(ownerLabel || "").trim() || "Propriétaire";
  if (level === "global") {
    return { rootLabel: zone, relativePath: "" };
  }
  if (level === "owner") {
    return { rootLabel: zone, relativePath: owner };
  }
  return coffreSharedBreadcrumbParts(currentPath, shareRoot, owner, zone);
}

/** Chemin relatif au point d’entrée du partage (fil d’Ariane destinataire). */
export function coffreSharedBreadcrumbParts(
  currentPath,
  shareRoot,
  ownerUsername = "",
  fallbackZoneLabel = COFFRE_SHARED_ZONE_LABEL
) {
  const root = String(shareRoot ?? "").trim();
  const path = String(currentPath ?? "").trim();
  if (!root) {
    return { rootLabel: fallbackZoneLabel, relativePath: path };
  }
  const rootName = root.split("/").filter(Boolean).pop() || root;
  const rootLabel = ownerUsername ? `${ownerUsername} — ${rootName}` : rootName;
  if (!path || path === root) {
    return { rootLabel, relativePath: "" };
  }
  if (path.startsWith(`${root}/`)) {
    return { rootLabel, relativePath: path.slice(root.length + 1) };
  }
  return { rootLabel, relativePath: path };
}

/** Convertit un segment de fil d’Ariane relatif en chemin propriétaire. */
export function coffreSharedNavigatePath(navRelativePath, shareRoot) {
  const root = String(shareRoot ?? "").trim();
  const nav = String(navRelativePath ?? "")
    .trim()
    .replace(/^\/+|\/+$/g, "");
  if (!root) return nav;
  if (!nav) return root;
  return `${root}/${nav}`;
}

/** Dossier parent d’un chemin relatif ("" = racine coffre). */
export function coffreEntryParentPath(relPath) {
  const p = String(relPath ?? "").replace(/\\/g, "/").trim();
  if (!p) return "";
  const i = p.lastIndexOf("/");
  return i < 0 ? "" : p.slice(0, i);
}

/**
 * Racines de navigation pour « Enregistrer sous » en zone partagée (toujours des dossiers).
 * @returns {{ listRoot: string, clampRoot: string }}
 */
export function coffreSharedSaveAsListContext(itemShareRoot, filePath, shareIsFolderFlag = false) {
  const root = String(itemShareRoot ?? "").trim();
  const file = String(filePath ?? "").trim();

  if (root && (shareIsFolderFlag || (file && file !== root && file.startsWith(`${root}/`)))) {
    return { listRoot: root, clampRoot: root };
  }

  if (root && file && root === file) {
    const parent = coffreEntryParentPath(file);
    return { listRoot: parent, clampRoot: parent };
  }

  if (root) {
    const parent = coffreEntryParentPath(root);
    if (parent !== root && parent !== "") {
      return { listRoot: parent, clampRoot: parent };
    }
    if (shareIsFolderFlag) {
      return { listRoot: root, clampRoot: root };
    }
    const fromFile = file ? coffreEntryParentPath(file) : "";
    return { listRoot: fromFile || parent || root, clampRoot: fromFile || parent || root };
  }

  const parent = coffreEntryParentPath(file);
  return { listRoot: parent, clampRoot: parent };
}

/** Racine d’arborescence pour copier-déplacer / picker (dossier partagé ou parent d’un fichier). */
export function coffreSharedPickerScopedRoot(shareRoot, shareIsFolder = false) {
  const root = String(shareRoot ?? "").trim();
  if (!root) return "";
  if (shareIsFolder) return root;
  return coffreEntryParentPath(root);
}

/** Lecture seule effective pour un élément (home virtuel ou navigation partagée). */
export function coffreEntrySharedReadOnly(
  entry,
  zonePermission = "read",
  browseZone = "vault",
  browseCtx = {}
) {
  if (!entry) return browseZone === "shared";
  if (entry.is_shared_owner_virtual) return true;
  const perm = coffreEntrySharedPermission(entry, zonePermission);
  if (browseZone === "shared" || entry.shared_owner_id != null || entry.is_shared_virtual) {
    const ownerId = browseCtx.sharedOwnerId ?? entry.shared_owner_id ?? null;
    const path = browseCtx.currentPath ?? "";
    return !coffreSharedAllowWrite(browseZone, ownerId, path, perm);
  }
  return false;
}

/**
 * Contexte arborescence / mkdir pour copier-déplacer dans un partage reçu.
 * @returns {null | { ownerId: number, scopedRootPath: string, scopedRootLabel: string, defaultDest: string, mkdirWriteOpts: object }}
 */
export function coffreSharedTransferPickerContext(entry, browse = {}) {
  const ownerId = coffreEntrySharedOwnerId(entry, browse.sharedOwnerId ?? null);
  if (ownerId == null) return null;
  if (browse.browseZone !== "shared" && !entry?.shared_owner_id && !entry?.is_shared_virtual) {
    return null;
  }
  if (
    coffreSharedBrowseLevel(browse.browseZone, browse.sharedOwnerId ?? ownerId, browse.currentPath) !==
    "content"
  ) {
    return null;
  }

  const entryPath = String(entry?.path ?? "").trim();
  const shareRoot = String(
    entry?.shared_share_root ??
      browse.sharedShareRoot ??
      (entry?.is_shared_virtual ? entryPath : "")
  ).trim();

  const shareIsFolder =
    (entry?.is_shared_virtual && entry?.kind === "folder") ||
    Boolean(browse.sharedShareIsFolder) ||
    (shareRoot !== "" && entryPath !== shareRoot && entryPath.startsWith(`${shareRoot}/`));

  const shareRootOrPath = shareRoot || entryPath;
  const listRoot = coffreSharedPickerScopedRoot(shareRootOrPath, shareIsFolder);

  let defaultDest = String(browse.currentPath ?? "").trim();
  const inShare =
    shareRoot &&
    (defaultDest === shareRoot ||
      defaultDest.startsWith(`${shareRoot}/`) ||
      coffrePathUnderSharedRoot(defaultDest, shareRoot));
  const inListRoot =
    listRoot === ""
      ? defaultDest !== ""
      : defaultDest === listRoot || defaultDest.startsWith(`${listRoot}/`);

  if (!defaultDest || (shareRoot && !inShare && !inListRoot) || (ownerId && !inListRoot && listRoot !== defaultDest)) {
    const parent = entryPath.includes("/") ? coffreEntryParentPath(entryPath) : "";
    defaultDest = parent || listRoot || "";
  }

  return {
    ownerId,
    scopedRootPath: listRoot,
    scopedRootLabel: browse.sharedZoneLabel || COFFRE_SHARED_ZONE_LABEL,
    defaultDest,
    mkdirWriteOpts: { zone: "shared", ownerId },
  };
}

/** Chemin de navigation partagé borné au point d’entrée du partage. */
export function coffreSharedClampBrowsePath(path, shareRoot, fallbackPath = "") {
  const p = String(path ?? "").trim();
  const root = String(shareRoot ?? "").trim();
  if (!root) return p || String(fallbackPath ?? "").trim();
  if (!p) return root;
  if (p === root || p.startsWith(`${root}/`)) return p;
  if (coffrePathUnderSharedRoot(p, root)) return p;
  const fb = String(fallbackPath ?? "").trim();
  if (fb && (fb === root || fb.startsWith(`${root}/`))) return fb;
  return root;
}

export function coffrePathUnderSharedRoot(path, shareRoot) {
  const p = String(path ?? "").trim();
  const root = String(shareRoot ?? "").trim();
  if (!root) return false;
  return p === root || p.startsWith(`${root}/`);
}

/** Remonter d’un niveau sans sortir du périmètre partagé. */
export function coffreSharedParentNavigatePath(currentPath, shareRoot) {
  const path = String(currentPath ?? "").trim();
  const root = String(shareRoot ?? "").trim();
  if (!root || path === root) return null;
  const slash = path.lastIndexOf("/");
  const parent = slash < 0 ? "" : path.slice(0, slash);
  if (coffrePathUnderSharedRoot(parent, root) && parent.length >= root.length) {
    return parent;
  }
  return root;
}

/** ID propriétaire pour un fichier/dossier partagé. */
export function coffreEntrySharedOwnerId(entry, sharedOwnerId = null) {
  if (entry?.shared_owner_id != null) return entry.shared_owner_id;
  if (sharedOwnerId != null) return sharedOwnerId;
  return null;
}

/** Permission effective sur un élément partagé (entrée ou zone courante). */
export function coffreEntrySharedPermission(entry, zonePermission = "read") {
  if (entry?.permission === "write" || entry?.shared_permission === "write") return "write";
  if (entry?.permission === "read" || entry?.shared_permission === "read") return "read";
  return zonePermission === "write" ? "write" : "read";
}

/** OnlyOffice en consultation seule pour un destinataire en lecture. */
export function coffreSharedOfficeViewOnly(browseZone, permission, entry = null) {
  if (browseZone !== "shared") return Boolean(entry?.officeViewOnly);
  return coffreEntrySharedPermission(entry, permission) !== "write";
}

export function coffreStampSharedEntry(entry, ownerId, permission = null) {
  if (!entry || ownerId == null) return entry;
  const next = entry.shared_owner_id === ownerId ? entry : { ...entry, shared_owner_id: ownerId };
  if (permission && !next.permission && !next.shared_permission) {
    return { ...next, shared_permission: permission };
  }
  return next;
}

export function coffreSaveTextRequestOpts(entry) {
  if (entry?.shared_owner_id) {
    return { zone: "shared", ownerId: entry.shared_owner_id };
  }
  return {};
}

/** Options mkdir / upload / create-file dans un partage reçu en écriture. */
export function coffreBrowseWriteRequestOpts(browseZone, sharedOwnerId) {
  if (browseZone === "shared" && sharedOwnerId) {
    return { zone: "shared", ownerId: sharedOwnerId };
  }
  return {};
}

/** Options suppression (corbeille / quarantaine / partage en écriture). */
export function coffreDeleteRequestOpts(browseZone, sharedOwnerId) {
  if (browseZone === "trash" || browseZone === "quarantine") {
    return { zone: browseZone };
  }
  return coffreBrowseWriteRequestOpts(browseZone, sharedOwnerId);
}

/** Suppression d’un élément partagé (home virtuel ou navigation chez le propriétaire). */
export function coffreDeleteRequestOptsForEntry(entry, browseZone, sharedOwnerId) {
  if (coffreEntryIsShortcut(entry)) {
    return { zone: "vault" };
  }
  const ownerId = coffreEntrySharedOwnerId(entry, sharedOwnerId);
  if (ownerId != null) {
    return { zone: "shared", ownerId };
  }
  return coffreDeleteRequestOpts(browseZone, sharedOwnerId);
}

/** Options API écriture partagée (renommer, déplacer, mkdir, etc.). */
export function coffreMutationRequestOptsForEntry(entry, browseZone, sharedOwnerId) {
  const ownerId = coffreEntrySharedOwnerId(entry, sharedOwnerId);
  if (ownerId != null) {
    return { zone: "shared", ownerId };
  }
  return coffreBrowseWriteRequestOpts(browseZone, sharedOwnerId);
}

/** Options API OnlyOffice (zone partagée, mode lecture/écriture). */
export function coffreEntryOfficeOpts(
  entry,
  { browseZone = "vault", sharedOwnerId = null, sharedPermission = "read", isMobile = false } = {}
) {
  const mobileView = isMobile ? { mode: "view", editor_type: "mobile" } : null;
  const withMobile = (base) => (mobileView ? { ...base, ...mobileView } : base);

  const ownerId = coffreEntrySharedOwnerId(entry, sharedOwnerId);
  const perm = coffreEntrySharedPermission(entry, sharedPermission);
  const sharedMode = coffreSharedOfficeViewOnly("shared", perm, entry) ? "view" : "edit";
  let share = null;
  if (ownerId != null) {
    share = { zone: "shared", ownerId, mode: sharedMode };
  } else if (browseZone === "shared" && sharedOwnerId) {
    share = { zone: "shared", ownerId: sharedOwnerId, mode: sharedMode };
  }

  if (entry?.officeViewOnly) {
    return withMobile(share ? { ...share, mode: "view" } : { mode: "view" });
  }
  if (share) return withMobile(share);
  return withMobile({});
}

/** Archive ZIP consultable dans le Coffre (pas de téléchargement). */
export function coffreIsZipArchiveEntry(entry) {
  if (!entry || entry.kind !== "file" || !entry.name) return false;
  const parts = String(entry.name).split(".");
  if (parts.length < 2) return false;
  return parts.pop().toLowerCase() === "zip";
}

/** Éléments d’une sélection qu’on peut mettre dans un ZIP du Coffre. */
export function coffreEntriesCompressible(entries, entryWriteBlocked) {
  const list = (entries || []).filter((e) => {
    if (!e?.path || coffreEntryIsShortcut(e)) return false;
    if (e.needs_unlock) return false;
    if (typeof entryWriteBlocked === "function" && entryWriteBlocked(e)) return false;
    return true;
  });
  if (list.length === 1 && coffreIsZipArchiveEntry(list[0])) return [];
  return list;
}

/** Nom proposé pour le ZIP (sans extension). */
export function coffreDefaultCompressZipStem(entries, currentPath = "") {
  if (Array.isArray(entries) && entries.length === 1 && entries[0]?.name) {
    return String(entries[0].name).replace(/\.zip$/i, "") || "archive";
  }
  const parent = String(currentPath || "")
    .split("/")
    .filter(Boolean)
    .pop();
  return parent || "archive";
}

/** Clé stable pour associer un rappel à un élément (liste, sidebar). */
export function coffreReminderItemKey(zone, ownerId, path) {
  return `${zone || "vault"}:${ownerId ?? ""}:${path || ""}`;
}

/** Clé d’enregistrement d’un rappel (path + zone) — indépendante du téléchargement. */
export function coffreEntryReminderStorage(entry, browseZone = "vault", sharedOwnerId = null) {
  if (!entry?.path) return null;
  if (coffreEntryIsShortcut(entry)) {
    return { path: entry.path, zone: "vault", ownerId: null };
  }
  const ownerId = coffreEntrySharedOwnerId(entry, sharedOwnerId);
  if (ownerId != null) {
    return { path: entry.path, zone: "shared", ownerId };
  }
  if (browseZone === "ged") {
    return { path: entry.path, zone: "ged", ownerId: null };
  }
  return { path: entry.path, zone: "vault", ownerId: null };
}

/** Rappel hérité enregistré sur la cible avant alias raccourci (migration douce). */
export function coffreEntryReminderLegacyLookup(entry, reminderLookup, browseZone = "vault") {
  if (!coffreEntryIsShortcut(entry) || entry?.shortcut_broken || !entry.shortcut_target?.path) {
    return null;
  }
  const t = entry.shortcut_target;
  const zone = t.zone === "shared" && t.owner_id ? "shared" : browseZone === "ged" ? "ged" : "vault";
  const ownerId = zone === "shared" && t.owner_id ? t.owner_id : null;
  return reminderLookup.get(coffreReminderItemKey(zone, ownerId, t.path)) ?? null;
}

/** Cible d’un raccourci (vault ou partage) — null si non autorisée (ex. dossier utilisateur virtuel). */
export function coffreEntryShortcutTarget(entry, browseZone = "vault", sharedOwnerId = null) {
  if (!entry?.path) return null;
  const path = String(entry.path).trim();
  if (
    entry.is_shared_owner_virtual ||
    path.startsWith(COFFRE_SHARED_OWNER_PATH_PREFIX)
  ) {
    return null;
  }
  const fromSharedBrowse =
    browseZone === "shared" || Boolean(entry.is_shared_virtual) || entry.shared_owner_id != null;
  if (fromSharedBrowse) {
    const ownerId = coffreEntrySharedOwnerId(entry, sharedOwnerId);
    if (ownerId == null || ownerId < 1) return null;
    return {
      path,
      zone: "shared",
      ownerId,
      kind: entry.kind === "folder" ? "folder" : "file",
    };
  }
  if (browseZone === "ged") return null;
  return {
    path,
    zone: "vault",
    ownerId: null,
    kind: entry.kind === "folder" ? "folder" : "file",
  };
}

/** Même périmètre que {@link coffreEntryReminderStorage} pour les rappels. */
export function coffreEntryReminderOpts(entry, browseZone = "vault", sharedOwnerId = null) {
  return coffreEntryReminderStorage(entry, browseZone, sharedOwnerId);
}

/** Nom court du dossier parent quand le partage est hérité (liste propriétaire). */
export function coffreEntryShareInheritedFolderName(entry) {
  const from = entry?.share_inherited_from;
  if (typeof from === "string" && from.trim() !== "") {
    const parts = from.split("/").filter(Boolean);
    return parts[parts.length - 1] || from.trim();
  }
  if (entry?.share_inherited && typeof entry?.path === "string" && entry.path.includes("/")) {
    const parts = entry.path.split("/").filter(Boolean);
    if (parts.length >= 2) {
      return parts[parts.length - 2];
    }
  }
  return "";
}

function coffreFormatShareRecipients(recipients) {
  if (!Array.isArray(recipients) || recipients.length === 0) return "";
  return recipients
    .map((r) => {
      const name = (r?.group_name || r?.username || "").trim();
      if (!name) return "";
      const perm =
        r?.permission === "write" && r?.viewer_master_required
          ? `${name} (écriture, MDP maître requis)`
          : r?.permission === "write"
            ? `${name} (écriture)`
            : r?.viewer_master_required
              ? `${name} (MDP maître requis)`
              : name;
      return perm;
    })
    .filter(Boolean)
    .join(", ");
}

/** Libellé colonne partage (liste fichiers). */
export function coffreEntrySharePartyLabel(entry, browseZone, sharedOwnerUsername = "") {
  if (browseZone === "shared") {
    return entry?.shared_owner_username || sharedOwnerUsername || "—";
  }
  if (entry?.share_excluded) {
    const via = entry?.share_inherited_from
      ? entry.share_inherited_from.split("/").pop() || entry.share_inherited_from
      : "";
    const inherited = coffreFormatShareRecipients(entry?.share_inherited_recipients);
    if (via && inherited) return `Exclu · via ${via} : ${inherited}`;
    if (via) return `Exclu · via ${via}`;
    return "Exclu du partage";
  }
  const recipients = entry?.share_recipients;
  const label = coffreFormatShareRecipients(recipients);
  if (!label) return "—";
  if (entry?.share_inherited && entry?.share_inherited_from) {
    const via = entry.share_inherited_from.split("/").pop() || entry.share_inherited_from;
    return `${label} · via ${via}`;
  }
  return label;
}

export function coffreRenameNameParts(name, kind) {
  const n = name || "";
  if (kind === "folder") {
    return { base: n, ext: "" };
  }
  const dot = n.lastIndexOf(".");
  if (dot <= 0) {
    return { base: n, ext: "" };
  }
  return { base: n.slice(0, dot), ext: n.slice(dot) };
}

/** Interdits Windows / sync agent : < > : " / \ | ? * et contrôles. */
const COFFRE_ILLEGAL_NAME_CHARS = /[<>:"/\\|?*\u0000-\u001F]/;
const COFFRE_ILLEGAL_NAME_CHARS_GLOBAL = /[<>:"/\\|?*\u0000-\u001F]/g;

export function coffreNameHasIllegalChars(name) {
  return COFFRE_ILLEGAL_NAME_CHARS.test(String(name ?? ""));
}

export function coffreStripIllegalNameChars(name) {
  return String(name ?? "").replace(COFFRE_ILLEGAL_NAME_CHARS_GLOBAL, "");
}

/**
 * Valide un nom de fichier ou dossier (segment unique).
 * @returns {{ ok: true, name: string } | { ok: false, message: string }}
 */
export function coffreValidateEntryName(raw) {
  const name = String(raw ?? "").trim();
  if (!name) {
    return { ok: false, message: "Indiquez un nom." };
  }
  if (name === "." || name === "..") {
    return { ok: false, message: "Nom réservé." };
  }
  if (name.startsWith(".")) {
    return { ok: false, message: "Le nom ne peut pas commencer par un point." };
  }
  if (coffreNameHasIllegalChars(name)) {
    return {
      ok: false,
      message: "Caractères interdits : < > : \" / \\ | ? *",
    };
  }
  if (/[. ]$/.test(name)) {
    return { ok: false, message: "Le nom ne peut pas se terminer par un point ou un espace." };
  }
  return { ok: true, name };
}

export function coffreRenameFinalName(baseInput, originalName, kind) {
  const base = (baseInput || "").trim();
  if (!base) return "";
  if (kind === "folder") return base;
  const { ext } = coffreRenameNameParts(originalName, kind);
  let stem = base;
  const dot = stem.lastIndexOf(".");
  if (dot > 0) {
    stem = stem.slice(0, dot);
  }
  return stem.trim() ? stem.trim() + ext : "";
}

/** Hint tooltip — uniquement si l’info n’est pas déjà visible à l’écran. */
export function coffreEntryUnlockHint(entry) {
  if (!entry?.needs_unlock) return null;
  if (entry?.shared_owner_id || entry?.is_shared_virtual) {
    return "Partage sensible — double-clic ou clic droit, puis votre mot de passe maître";
  }
  return "Double-clic ou clic droit, puis mot de passe maître pour ouvrir";
}

/** Nom tronqué (line-clamp / truncate) — afficher le nom complet au survol. */
export function coffreTruncatedLabelHint(label, minChars = 28) {
  const t = (label || "").trim();
  if (t.length <= minChars) return null;
  return t;
}

/** Couleur du nom — orange verrouillé, vert déverrouillé (liste / Miller / mobile). */
export function coffreEntryNameStatusClass(entry) {
  if (entry?.needs_unlock) return "coffre-entry-name--locked";
  if (entry?.is_protected) return "coffre-entry-name--protected";
  return "";
}

/** Classes visuelles (liste = léger, grille = plus marqué). */
export function coffreEntryProtectedShellClass(entry, layout = "grid") {
  if (!entry?.is_protected) {
    return "";
  }
  if (layout === "list") {
    return "";
  }
  if (layout === "grid") {
    if (!entry?.is_protected) return "";
    return entry.needs_unlock ? "border-l-2 border-l-amber-400" : "border-l-2 border-l-emerald-500";
  }
  return cn(
    entry.needs_unlock
      ? "border-amber-300/90 bg-amber-50/70 dark:border-amber-500/50 dark:bg-amber-500/10"
      : "border-emerald-300/80 bg-emerald-50/50 dark:border-emerald-500/40 dark:bg-emerald-500/10",
    entry.needs_unlock && "shadow-[inset_0_0_0_1px_rgba(245,158,11,0.2)]"
  );
}

/** Panneau d’aperçu latéral — vue fractionnée et Miller Columns. */
export function coffreBrowseHasPreviewPane(mode) {
  return mode === "split" || mode === "columns";
}

/** Vue affichée : Miller Columns uniquement en Mon coffre (hors entrant/sortie). */
export function coffreEffectiveBrowseViewMode(mode, { browseZone, gedExchangeSide, isMobile = false } = {}) {
  let effective = mode;
  if (effective === "columns" && (browseZone !== "vault" || gedExchangeSide)) {
    effective = "list";
  }
  if (isMobile && (effective === "split" || effective === "columns")) {
    effective = "list";
  }
  return effective;
}

export function coffrePendingActionAfterUnlock(entry, onlyofficeConfigured, browseZone = "vault") {
  if (!entry) return null;
  const zone = browseZone === "shared" || entry?.shared_owner_id ? "shared" : browseZone;
  if (entry.kind === "folder") {
    return { type: "navigate", path: entry.path, entry, zone };
  }
  if (coffreCanPreviewFile(entry)) {
    return { type: "preview_modal", path: entry.path, entry };
  }
  if (onlyofficeConfigured && coffreCanOpenInOffice(entry)) {
    return { type: "office_edit", path: entry.path, entry };
  }
  return null;
}

export function coffreEntryGridActiveClass(entry, activeFilePath) {
  if (!activeFilePath || entry.path !== activeFilePath) return "";
  return "coffre-entry-grid-active";
}

/** Surbrillance liste — dossiers : accent UI ; ambre si verrouillé. */
export function coffreEntryListHighlightClass(entry, activeFilePath = "") {
  if (activeFilePath && entry.path === activeFilePath && entry.kind === "file") {
    return "coffre-entry-preview-active";
  }
  if (entry.kind === "folder") {
    if (entry.needs_unlock) return "";
    return "coffre-entry-folder-hover";
  }
  return "hover:bg-slate-50/80 dark:hover:bg-slate-800/50";
}

export function coffreTransferDestBlocked(folderPath, sourceEntryOrList) {
  const sources = Array.isArray(sourceEntryOrList)
    ? sourceEntryOrList
    : sourceEntryOrList
      ? [sourceEntryOrList]
      : [];
  for (const sourceEntry of sources) {
    if (!sourceEntry || sourceEntry.kind !== "folder") continue;
    const src = sourceEntry.path || "";
    if (!src) continue;
    if (folderPath === src) return true;
    if (folderPath.startsWith(`${src}/`)) return true;
  }
  return false;
}

export function coffreEntryInternalDropClass(entry, dropTargetPath) {
  if (entry.kind !== "folder" || dropTargetPath !== entry.path) return "";
  return "coffre-entry-drop-target";
}

export function coffreRootDropTargetClass(isTarget) {
  if (!isTarget) return "";
  return "coffre-entry-drop-target";
}

export function coffreFormatDestPathLabel(rootLabel, path, gedVaultRoot = "") {
  const p = (path || "").trim();
  if (!p) return rootLabel;
  const gedRoot = (gedVaultRoot || "").trim();
  if (gedRoot && (p === gedRoot || p.startsWith(`${gedRoot}/`))) {
    const rel = p === gedRoot ? "" : p.slice(gedRoot.length + 1);
    return rel ? `${gedRoot} / ${rel.split("/").join(" / ")}` : gedRoot;
  }
  return `${rootLabel} / ${p.split("/").join(" / ")}`;
}

/** Libellé destination copier/déplacer (Mon coffre, Ma GED ou partage reçu en écriture). */
export function coffreFormatTransferDestLabel({
  rootLabel,
  path,
  gedVaultRoot = "",
  destTargetKey = "vault",
  sharedPicker = null,
  writableShareTargets = [],
}) {
  if (sharedPicker) {
    const label = sharedPicker.scopedRootLabel || COFFRE_SHARED_ZONE_LABEL;
    const p = String(path ?? "").trim();
    const root = String(sharedPicker.scopedRootPath ?? "").trim();
    if (!p) return label;
    if (root && (p === root || p.startsWith(`${root}/`))) {
      const rel = p === root ? "" : p.slice(root.length + 1);
      return rel ? `${label} / ${rel.split("/").join(" / ")}` : label;
    }
    return `${label} / ${p.split("/").join(" / ")}`;
  }
  if (destTargetKey !== "vault" && destTargetKey !== "ged") {
    const share = (writableShareTargets || []).find((t) => t.key === destTargetKey);
    if (share) {
      const label = share.scopedRootLabel || `Partagé · ${share.ownerLabel}`;
      const p = String(path ?? "").trim();
      const root = String(share.scopedRootPath ?? "").trim();
      if (!p) return label;
      if (root && (p === root || p.startsWith(`${root}/`))) {
        const rel = p === root ? "" : p.slice(root.length + 1);
        return rel ? `${label} / ${rel.split("/").join(" / ")}` : label;
      }
      return `${label} / ${p.split("/").join(" / ")}`;
    }
  }
  if (destTargetKey === "ged") {
    const gedRoot = String(gedVaultRoot ?? "").trim();
    if (gedRoot) return coffreFormatDestPathLabel(gedRoot, path, gedRoot);
  }
  return coffreFormatDestPathLabel(rootLabel, path, gedVaultRoot);
}

/** Cibles partagées en écriture pour le picker copier/déplacer. */
export function coffreMapWritableShareTargets(received = []) {
  const rows = Array.isArray(received) ? received : [];
  const targets = [];
  for (const row of rows) {
    if ((row.permission || "read") !== "write" || row.needs_unlock) continue;
    const ownerId = Number(row.owner_user_id);
    const itemPath = String(row.item_path ?? "").trim();
    if (!ownerId || !itemPath) continue;
    const ownerLabel =
      String(row.owner_display_name ?? "").trim() ||
      String(row.owner_username ?? "").trim() ||
      "Utilisateur";
    const shareName =
      String(row.name ?? "").trim() || itemPath.split("/").filter(Boolean).pop() || "Partage";
    const isFolder = Boolean(row.is_folder);
    const scopedRootPath = coffreSharedPickerScopedRoot(itemPath, isFolder);
    targets.push({
      key: `${ownerId}:${itemPath}`,
      ownerId,
      ownerLabel,
      shareName,
      itemPath,
      isFolder,
      scopedRootPath,
      scopedRootLabel: `Partagé · ${ownerLabel} / ${shareName}`,
      defaultDest: scopedRootPath,
      mkdirWriteOpts: { zone: "shared", ownerId },
    });
  }
  targets.sort((a, b) =>
    `${a.ownerLabel} ${a.shareName}`.localeCompare(`${b.ownerLabel} ${b.shareName}`, "fr", {
      sensitivity: "base",
    })
  );
  return targets;
}

/** Regroupe les partages en écriture par propriétaire (accordéon UI). */
export function coffreGroupWritableShareTargetsByOwner(targets = []) {
  const rows = Array.isArray(targets) ? targets : [];
  const byOwner = new Map();
  for (const share of rows) {
    const ownerId = Number(share.ownerId);
    if (!ownerId) continue;
    if (!byOwner.has(ownerId)) {
      byOwner.set(ownerId, {
        ownerId,
        ownerLabel: share.ownerLabel || "Utilisateur",
        shares: [],
      });
    }
    byOwner.get(ownerId).shares.push(share);
  }
  return Array.from(byOwner.values()).sort((a, b) =>
    a.ownerLabel.localeCompare(b.ownerLabel, "fr", { sensitivity: "base" })
  );
}

/** Options API copier/déplacer (source + destination éventuelle partagée). */
export function coffreTransferRequestOpts(entry, browseZone, sharedOwnerId, modal, destTargetKey = "vault") {
  const sourceOpts = coffreMutationRequestOptsForEntry(entry, browseZone, sharedOwnerId);
  if (modal?.sharedPicker) {
    return modal.sharedPicker.mkdirWriteOpts ?? sourceOpts;
  }
  if (destTargetKey === "vault" || destTargetKey === "ged") {
    if (browseZone === "ged" && destTargetKey === "ged") {
      return { zone: "ged" };
    }
    return sourceOpts;
  }
  const share = (modal?.writableShareTargets || []).find((t) => t.key === destTargetKey);
  if (!share) return sourceOpts;
  return {
    ...sourceOpts,
    destZone: "shared",
    destOwnerId: share.ownerId,
  };
}
